import "./App.css";
import React, { Suspense, lazy, useState } from "react";
import { ConfigProvider } from "antd";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { store } from "./store";

import { Provider } from "react-redux";
import setAuthToken from "./utils/setAuthToken";
import AdminRoute from "./routes/AdminRoute";
import Sidebar from "./layout/Sidebar";
import Navbar from "./layout/Navbar";
import { saveConfig, setAdmin } from "./actions/AuthAction";
import { setConfig } from "./reducers/configReducer";

import { isPermitted } from "./utils/PermissionManager";
import { setProfile } from "./reducers/authReducer";
import Bottombar from "./layout/BottomBar";
import { siteInfo } from "./utils/SiteInfo";

const CampaignDetails = lazy(() => import("./pages/CampaignDetails"));
const Reviews = lazy(() => import("./components/Reviews"));
const Support = lazy(() => import("./pages/Support"));

const Vendors = lazy(() => import("./pages/Vendors"));
const Purchase = lazy(() => import("./pages/Purchases"));
const VendorOrderDetails = lazy(() =>
  import("./components/VendorOrderDetails")
);

const PosOrders = lazy(() => import("./pages/PosOrders"));
const Calls = lazy(() => import("./pages/Calls"));
const CreateMasterProduct = lazy(() => import("./pages/CreateMasterProduct"));
const ManageProductNew = lazy(() => import("./pages/ManageProductNew"));
const CreateOrder = lazy(() => import("./pages/CreateOrder"));
const PendingOrders = lazy(() => import("./pages/PendingOrders"));
const ConfirmedOrders = lazy(() => import("./pages/ConfirmedOrders"));
const BulkPrinting = lazy(() => import("./pages/BulkPrinting"));
const PackingOrders = lazy(() => import("./pages/PackingOrders"));
const ShippedOrders = lazy(() => import("./pages/ShippedOrders"));
const DeliveredOrders = lazy(() => import("./pages/DeliveredOrders"));
const Attributes = lazy(() => import("./pages/Attributes"));
const ManageAttribute = lazy(() => import("./pages/ManageAttribute"));
const Campaigns = lazy(() => import("./pages/Campaigns"));
const BulkDiscount = lazy(() => import("./pages/BulkDiscount"));
const Invoice = lazy(() => import("./pages/Invoice"));
const BlogCategories = lazy(() => import("./pages/BlogCategories"));
const BlogPosts = lazy(() => import("./pages/BlogPosts"));
const ManageBlog = lazy(() => import("./pages/ManageBlog"));
const Payments = lazy(() => import("./pages/Payments"));
const MonthlyReport = lazy(() => import("./pages/MonthlyReport"));
const CustomerReport = lazy(() => import("./pages/CustomerReport"));
const ProductReport = lazy(() => import("./pages/ProductReport"));
const HomeBlocks = lazy(() => import("./pages/HomeBlocks"));
const Categories = lazy(() => import("./pages/Categories"));
const Products = lazy(() => import("./pages/Products"));
const Brands = lazy(() => import("./pages/Brands"));
const SubCategories = lazy(() => import("./pages/SubCategories"));
const Coupon = lazy(() => import("./pages/Coupon"));
const Banners = lazy(() => import("./pages/Banners"));
const MetaSettings = lazy(() => import("./pages/MetaSettings"));
const Pages = lazy(() => import("./pages/Pages"));
const Orders = lazy(() => import("./pages/Orders"));
const ManageOrder = lazy(() => import("./pages/ManageOrder"));

const Customers = lazy(() => import("./pages/Customers"));
const Login = lazy(() => import("./pages/Login"));
const Admins = lazy(() => import("./pages/Admins"));
const Profile = lazy(() => import("./pages/Profile"));
const ComingSoon = lazy(() => import("./pages/ComingSoon"));

if (localStorage.config) {
  let config = JSON.parse(localStorage.config);
  let profile = config.profile;
  store.dispatch(setProfile(profile));
  store.dispatch(setConfig(config));
}

if (localStorage[siteInfo.sitekey]) {
  setAuthToken(localStorage[siteInfo.sitekey]);
  store.dispatch(setAdmin(true));
  store.dispatch(saveConfig());
}

const Dashboard = lazy(() => import("./pages/Dashboard"));

function App() {
  const client = window.screen.width > 768 ? "web" : "app";
  const [show, setshow] = useState(client === "web" ? false : true);
  const [trigger, settrigger] = useState(false);

  const id = store.getState().auth?.profile?._id;

  const token = localStorage[siteInfo.sitekey];

  const roles = store.getState().auth?.profile?.roles
    ? store.getState().auth?.profile?.roles
    : [];

  return (
    <Provider store={store}>
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: "#F15629",
          },
        }}
      >
        {token ? (
          <BrowserRouter>
            <Suspense fallback={<div className="pageProgress"></div>}>
              <div className="App">
                <Sidebar state={{ show, setshow }} />
                <div>
                  <Navbar state={{ settrigger, setshow, show }} />
                  {id && <Bottombar />}

                  <Routes>
                    <Route
                      exact
                      path="/"
                      element={
                        <AdminRoute
                          state={{ show, trigger }}
                          component={<Dashboard />}
                        />
                      }
                    />

                    <Route
                      exact
                      path="/profile"
                      element={
                        <AdminRoute
                          state={{ show, trigger }}
                          component={<Profile />}
                        />
                      }
                    />

                    {isPermitted(roles, ["admin", "customercare"]) && (
                      <Route
                        exact
                        path="/customers"
                        element={
                          <AdminRoute
                            state={{ show, trigger }}
                            component={<Customers />}
                          />
                        }
                      />
                    )}
                    {isPermitted(roles, ["admin", "marketer"]) && (
                      <Route
                        exact
                        path="/pages"
                        element={
                          <AdminRoute
                            state={{ show, trigger }}
                            component={<Pages />}
                          />
                        }
                      />
                    )}
                    {isPermitted(roles, ["admin", "marketer"]) && (
                      <Route
                        exact
                        path="/banner/:id?"
                        element={
                          <AdminRoute
                            state={{ show, trigger }}
                            component={<Banners />}
                          />
                        }
                      />
                    )}
                    {isPermitted(roles, ["admin", "inventory"]) && (
                      <Route
                        exact
                        path="/manage-attribute/:id"
                        element={
                          <AdminRoute
                            state={{ show, trigger }}
                            component={<ManageAttribute />}
                          />
                        }
                      />
                    )}
                    {isPermitted(roles, ["admin", "inventory"]) && (
                      <Route
                        exact
                        path="/categories"
                        element={
                          <AdminRoute
                            state={{ show, trigger }}
                            component={<Categories />}
                          />
                        }
                      />
                    )}

                    {isPermitted(roles, ["admin", "inventory"]) && (
                      <Route
                        exact
                        path="/attributes"
                        element={
                          <AdminRoute
                            state={{ show, trigger }}
                            component={<Attributes />}
                          />
                        }
                      />
                    )}
                    {isPermitted(roles, ["admin", "marketer"]) && (
                      <Route
                        exact
                        path="/coupon"
                        element={
                          <AdminRoute
                            state={{ show, trigger }}
                            component={<Coupon />}
                          />
                        }
                      />
                    )}
                    {isPermitted(roles, ["admin", "marketer"]) && (
                      <Route
                        exact
                        path="/campaigns"
                        element={
                          <AdminRoute
                            state={{ show, trigger }}
                            component={<Campaigns />}
                          />
                        }
                      />
                    )}
                    {isPermitted(roles, ["admin", "marketer"]) && (
                      <Route
                        exact
                        path="/campaign/:code"
                        element={
                          <AdminRoute
                            state={{ show, trigger }}
                            component={<CampaignDetails />}
                          />
                        }
                      />
                    )}
                    {isPermitted(roles, ["admin", "customercare"]) && (
                      <Route
                        exact
                        path="/calls"
                        element={
                          <AdminRoute
                            state={{ show, trigger }}
                            component={<Calls />}
                          />
                        }
                      />
                    )}
                    {isPermitted(roles, ["admin", "marketer"]) && (
                      <Route
                        exact
                        path="/bulk-discount"
                        element={
                          <AdminRoute
                            state={{ show, trigger }}
                            component={<BulkDiscount />}
                          />
                        }
                      />
                    )}
                    {isPermitted(roles, ["admin", "marketer"]) && (
                      <Route
                        exact
                        path="/home-block"
                        element={
                          <AdminRoute
                            state={{ show, trigger }}
                            component={<HomeBlocks />}
                          />
                        }
                      />
                    )}
                    {isPermitted(roles, ["admin"]) && (
                      <Route
                        exact
                        path="/reviews"
                        element={
                          <AdminRoute
                            state={{ show, trigger }}
                            component={<Reviews />}
                          />
                        }
                      />
                    )}
                    {isPermitted(roles, ["admin", "customercare"]) && (
                      <Route
                        exact
                        path="/support"
                        element={
                          <AdminRoute
                            state={{ show, trigger }}
                            component={<Support />}
                          />
                        }
                      />
                    )}
                    {isPermitted(roles, ["admin", "marketer"]) && (
                      <Route
                        exact
                        path="/settings/:id?"
                        element={
                          <AdminRoute
                            state={{ show, trigger }}
                            component={<MetaSettings />}
                          />
                        }
                      />
                    )}
                    {isPermitted(roles, ["admin", "inventory"]) && (
                      <Route
                        exact
                        path="/category/:id"
                        element={
                          <AdminRoute
                            state={{ show, trigger }}
                            component={<SubCategories />}
                          />
                        }
                      />
                    )}
                    {isPermitted(roles, ["admin", "inventory"]) && (
                      <Route
                        exact
                        path="/brands"
                        element={
                          <AdminRoute
                            state={{ show, trigger }}
                            component={<Brands />}
                          />
                        }
                      />
                    )}
                    {isPermitted(roles, ["admin", "inventory"]) && (
                      <Route
                        exact
                        path="/product/:type/:id"
                        element={
                          <AdminRoute
                            state={{ show, trigger }}
                            component={<ManageProductNew />}
                          />
                        }
                      />
                    )}
                    {isPermitted(roles, ["admin", "inventory"]) && (
                      <Route
                        exact
                        path="/create-product"
                        element={
                          <AdminRoute
                            state={{ show, trigger }}
                            component={<CreateMasterProduct />}
                          />
                        }
                      />
                    )}
                    {isPermitted(roles, ["admin", "customercare"]) && (
                      <Route
                        exact
                        path="/create-order"
                        element={
                          <AdminRoute
                            state={{ show, trigger }}
                            component={<CreateOrder />}
                          />
                        }
                      />
                    )}
                    {isPermitted(roles, [
                      "admin",
                      "inventory",
                      "customercare",
                    ]) && (
                      <Route
                        exact
                        path="/products"
                        element={
                          <AdminRoute
                            state={{ show, trigger }}
                            component={<Products />}
                          />
                        }
                      />
                    )}
                    {isPermitted(roles, ["admin", "customercare"]) && (
                      <Route
                        exact
                        path="/orders"
                        element={
                          <AdminRoute
                            state={{ show, trigger }}
                            component={<Orders />}
                          />
                        }
                      />
                    )}
                    {isPermitted(roles, ["admin", "customercare"]) && (
                      <Route
                        exact
                        path="/pos-orders"
                        element={
                          <AdminRoute
                            state={{ show, trigger }}
                            component={<PosOrders />}
                          />
                        }
                      />
                    )}
                    {isPermitted(roles, ["admin", "customercare"]) && (
                      <Route
                        exact
                        path="/pending-orders"
                        element={
                          <AdminRoute
                            state={{ show, trigger }}
                            component={<PendingOrders />}
                          />
                        }
                      />
                    )}
                    {isPermitted(roles, ["admin", "customercare"]) && (
                      <Route
                        exact
                        path="/confirmed-orders"
                        element={
                          <AdminRoute
                            state={{ show, trigger }}
                            component={<ConfirmedOrders />}
                          />
                        }
                      />
                    )}
                    {isPermitted(roles, ["admin", "customercare"]) && (
                      <Route
                        exact
                        path="/packing-orders"
                        element={
                          <AdminRoute
                            state={{ show, trigger }}
                            component={<PackingOrders />}
                          />
                        }
                      />
                    )}
                    {isPermitted(roles, ["admin", "customercare"]) && (
                      <Route
                        exact
                        path="/shipped-orders"
                        element={
                          <AdminRoute
                            state={{ show, trigger }}
                            component={<ShippedOrders />}
                          />
                        }
                      />
                    )}
                    {isPermitted(roles, ["admin", "customercare"]) && (
                      <Route
                        exact
                        path="/delivered-orders"
                        element={
                          <AdminRoute
                            state={{ show, trigger }}
                            component={<DeliveredOrders />}
                          />
                        }
                      />
                    )}
                    <Route
                      exact
                      path="/order/:code"
                      element={
                        <AdminRoute
                          state={{ show, trigger }}
                          component={<ManageOrder />}
                        />
                      }
                    />
                    {isPermitted(roles, ["admin", "accountant"]) && (
                      <Route
                        exact
                        path="/purchase/:code"
                        element={
                          <AdminRoute
                            state={{ show, trigger }}
                            component={<VendorOrderDetails />}
                          />
                        }
                      />
                    )}
                    {isPermitted(roles, [
                      "admin",
                      "customercare",
                      "delivery",
                    ]) && (
                      <Route
                        exact
                        path="/invoice/:code"
                        element={
                          <AdminRoute
                            state={{ show, trigger }}
                            component={<Invoice />}
                          />
                        }
                      />
                    )}

                    {isPermitted(roles, [
                      "admin",
                      "customercare",
                      "delivery",
                    ]) && (
                      <Route
                        exact
                        path="/bulk-print/:code"
                        element={
                          <AdminRoute
                            state={{ show, trigger }}
                            component={<BulkPrinting />}
                          />
                        }
                      />
                    )}

                    {isPermitted(roles, ["admin", "accountant"]) && (
                      <Route
                        exact
                        path="/payments"
                        element={
                          <AdminRoute
                            state={{ show, trigger }}
                            component={<Payments />}
                          />
                        }
                      />
                    )}
                    {isPermitted(roles, ["admin", "accountant"]) && (
                      <Route
                        exact
                        path="/purchase"
                        element={
                          <AdminRoute
                            state={{ show, trigger }}
                            component={<Purchase />}
                          />
                        }
                      />
                    )}

                    {isPermitted(roles, ["admin", "accountant"]) && (
                      <Route
                        exact
                        path="/order-report"
                        element={
                          <AdminRoute
                            state={{ show, trigger }}
                            component={<MonthlyReport />}
                          />
                        }
                      />
                    )}
                    {isPermitted(roles, ["admin", "accountant"]) && (
                      <Route
                        exact
                        path="/customer-report"
                        element={
                          <AdminRoute
                            state={{ show, trigger }}
                            component={<CustomerReport />}
                          />
                        }
                      />
                    )}
                    {isPermitted(roles, ["admin", "accountant"]) && (
                      <Route
                        exact
                        path="/product-report"
                        element={
                          <AdminRoute
                            state={{ show, trigger }}
                            component={<ProductReport />}
                          />
                        }
                      />
                    )}
                    {isPermitted(roles, ["admin", "accountant"]) && (
                      <Route
                        exact
                        path="/vendors"
                        element={
                          <AdminRoute
                            state={{ show, trigger }}
                            component={<Vendors />}
                          />
                        }
                      />
                    )}

                    {isPermitted(roles, ["admin"]) && (
                      <Route
                        exact
                        path="/admins"
                        element={
                          <AdminRoute
                            state={{ show, trigger }}
                            component={<Admins />}
                          />
                        }
                      />
                    )}

                    <Route exact path="/login" element={<Login />} />
                    <Route
                      path="*"
                      element={
                        <AdminRoute
                          state={{ show, trigger }}
                          component={
                            <div
                              style={{
                                height: "calc(100vh - 78px)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "#fff",
                              }}
                            >
                              <ComingSoon />
                            </div>
                          }
                        />
                      }
                    />
                  </Routes>
                </div>
              </div>
            </Suspense>
          </BrowserRouter>
        ) : (
          <BrowserRouter>
            <Routes>
              <Route path="*" element={<Login />} />
            </Routes>
          </BrowserRouter>
        )}
      </ConfigProvider>
    </Provider>
  );
}

export default App;
